import axios, { AxiosError, type AxiosInstance } from "axios";
import type { ApiResponse } from "@marketing/shared";

const BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:4100/api/v1";

const ACCESS_KEY = "cart10mkt.access";
const REFRESH_KEY = "cart10mkt.refresh";
const WORKSPACE_KEY = "cart10mkt.workspace";

export function setTokens(t: { accessToken: string; refreshToken: string }): void {
  localStorage.setItem(ACCESS_KEY, t.accessToken);
  localStorage.setItem(REFRESH_KEY, t.refreshToken);
}
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(WORKSPACE_KEY);
}
export const getAccessToken = () => localStorage.getItem(ACCESS_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);

/** Active workspace — sent on every request so the API can scope the query. */
export const getActiveWorkspace = () => localStorage.getItem(WORKSPACE_KEY);
export function setActiveWorkspace(id: string | null): void {
  if (id) localStorage.setItem(WORKSPACE_KEY, id);
  else localStorage.removeItem(WORKSPACE_KEY);
}

export const api: AxiosInstance = axios.create({ baseURL: BASE_URL });

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const ws = getActiveWorkspace();
  if (ws) config.headers["X-Workspace-Id"] = ws;
  return config;
});

// Single-flight refresh: a burst of 401s queues behind one refresh call
// instead of each firing its own and invalidating the others, since the
// API rotates the refresh token on every exchange.
let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  try {
    const { data } = await axios.post<
      ApiResponse<{ accessToken: string; refreshToken: string }>
    >(`${BASE_URL}/auth/refresh`, { refreshToken });
    if (!data.success || !data.data) return null;
    setTokens(data.data);
    return data.data.accessToken;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as (typeof error.config & { _retried?: boolean }) | undefined;
    if (error.response?.status !== 401 || !original || original._retried) {
      return Promise.reject(error);
    }
    original._retried = true;

    refreshInFlight ??= refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
    const token = await refreshInFlight;

    if (!token) {
      clearTokens();
      if (!location.pathname.startsWith("/login")) location.assign("/login");
      return Promise.reject(error);
    }
    original.headers = original.headers ?? {};
    original.headers.Authorization = `Bearer ${token}`;
    return api.request(original);
  },
);

/**
 * The most useful message the server sent, for showing to a person.
 *
 * Axios puts "Request failed with status code 422" in `error.message`, which
 * tells the user nothing — the actual explanation is in the response body. A
 * zod rejection carries one entry per bad field under `errors`, and those are
 * written to be read ("A 12-hour gap across 1 active day allows about 2 posts a
 * week…"), so they are preferred over the generic "Validation failed" above
 * them.
 */
export function apiErrorMessage(err: unknown, fallback = "Something went wrong"): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string; errors?: Array<{ path?: string; message?: string }> }
      | undefined;

    const first = data?.errors?.find((e) => e.message)?.message;
    if (first) return first;
    if (data?.message) return data.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** Unwraps the API envelope, throwing on a failure response. */
export async function unwrap<T>(p: Promise<{ data: ApiResponse<T> }>): Promise<T> {
  const res = await p;
  if (!res.data.success || res.data.data === undefined) {
    throw new Error(res.data.message ?? "Request failed");
  }
  return res.data.data;
}
